import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { useState } from "react"
import { useCustomerDetail, usePatchCustomer } from "../../../api/customersApi"
import type {
  CustomerOut,
  CustomerUpdateIn,
} from "../../../api/types/masterData"
import { AdditionalAttributes } from "../../shared/AdditionalAttributes"
import { CollapsibleSection } from "../../shared/CollapsibleSection"
import { EditActionBar } from "../../shared/EditActionBar"
import type { FieldDef } from "../../shared/FieldsGrid"
import { FieldsGrid } from "../../shared/FieldsGrid"
import { FieldsSectionSkeleton } from "../../shared/FieldsSectionSkeleton"
import { EntityFieldsHeader } from "../EntityFieldsHeader"
import {
  useEntityFieldsState,
  type EntityFieldsState,
} from "../useEntityFieldsState"
import { useScrollIntoViewOnEdit } from "../useScrollIntoViewOnEdit"

interface CustomerFieldsState
  extends Omit<EntityFieldsState<CustomerOut, CustomerUpdateIn>, "entity"> {
  customer: CustomerOut | undefined
}

export function useCustomerFieldsState(): CustomerFieldsState {
  const { entity: customer, ...rest } = useEntityFieldsState(
    useCustomerDetail,
    usePatchCustomer,
  )
  return { customer, ...rest }
}

interface SectionProps {
  state: CustomerFieldsState
}

export function CustomerFieldsHeader({ state }: SectionProps) {
  return (
    <EntityFieldsHeader
      entity={state.customer}
      isLoading={state.isLoading}
      editing={state.editing}
      setEditing={state.setEditing}
      labelKey="MC_MD_TAB_CUSTOMERS"
      name={state.customer?.name}
      subtitle={state.customer?.customerNumber ?? state.customer?.id}
    />
  )
}

function triStateValue(value: boolean | null | undefined): string {
  if (value == null) return "default"
  return value ? "yes" : "no"
}

export function CustomerFieldsBody({ state }: SectionProps) {
  const { translate } = useTranslation()
  const {
    customer,
    isLoading,
    editing,
    edits,
    setEdits,
    isPending,
    handleSave,
    handleCancel,
  } = state
  const [fieldsOpen, setFieldsOpen] = useState(false)
  const fieldsRef = useScrollIntoViewOnEdit<HTMLDivElement>(editing)

  if (isLoading) return <FieldsSectionSkeleton count={4} />
  if (!customer) return null

  const fields: FieldDef[] = [
    {
      label: translate("MC_MD_FIELD_CUSTOMER_NUMBER"),
      value: (
        <span className="font-mono">{customer.customerNumber ?? "—"}</span>
      ),
    },
    {
      label: translate("MC_MD_FIELD_NAME"),
      value: editing ? (
        <Input
          value={edits.name ?? customer.name}
          onChange={(e) =>
            setEdits((prev) => ({ ...prev, name: e.target.value }))
          }
          className="h-7 w-full border-grey4 bg-grey2 text-xs text-white shadow-none focus-visible:border-yellow1 focus-visible:ring-0 focus-visible:ring-offset-0"
        />
      ) : (
        customer.name
      ),
    },
    {
      label: translate("MC_MD_FIELD_EMAIL"),
      value: editing ? (
        <Input
          value={edits.email ?? customer.email ?? ""}
          onChange={(e) =>
            setEdits((prev) => ({ ...prev, email: e.target.value }))
          }
          className="h-7 w-full border-grey4 bg-grey2 text-xs text-white shadow-none focus-visible:border-yellow1 focus-visible:ring-0 focus-visible:ring-offset-0"
        />
      ) : (
        (customer.email ?? "—")
      ),
      // Same reason as the supplier panel: an address does not survive the
      // two-column width, and half an address is the wrong address.
      fullWidth: true,
    },
    {
      label: translate("MC_MD_FIELD_DECLARATION_FRESHNESS"),
      value: editing ? (
        <Input
          type="number"
          value={
            edits.declarationFreshnessDays ??
            customer.declarationFreshnessDays ??
            ""
          }
          onChange={(e) =>
            setEdits((prev) => ({
              ...prev,
              declarationFreshnessDays: e.target.value
                ? Number(e.target.value)
                : undefined,
            }))
          }
          className="h-7 w-full border-grey4 bg-grey2 text-xs text-white shadow-none focus-visible:border-yellow1 focus-visible:ring-0 focus-visible:ring-offset-0"
        />
      ) : (
        <span className="font-mono">
          {customer.declarationFreshnessDays != null
            ? translate("MC_MD_UNIT_DAYS", {
                count: customer.declarationFreshnessDays,
              })
            : "—"}
        </span>
      ),
    },
    {
      label: translate("MC_MD_FIELD_CONCENTRATION_THRESHOLD"),
      value: editing ? (
        <Input
          type="number"
          value={
            edits.concentrationThresholdPct ??
            customer.concentrationThresholdPct ??
            ""
          }
          onChange={(e) =>
            setEdits((prev) => ({
              ...prev,
              concentrationThresholdPct: e.target.value
                ? Number(e.target.value)
                : undefined,
            }))
          }
          className="h-7 w-full border-grey4 bg-grey2 text-xs text-white shadow-none focus-visible:border-yellow1 focus-visible:ring-0 focus-visible:ring-offset-0"
        />
      ) : (
        <span className="font-mono">
          {customer.concentrationThresholdPct != null
            ? `${customer.concentrationThresholdPct}%`
            : "—"}
        </span>
      ),
    },
    {
      // Q6 tri-state: null = org default, true/false = per-customer.
      label: translate("MC_TPL_DECL_PRINT_VALID"),
      value: editing ? (
        <Select
          value={triStateValue(
            "printValidUntil" in edits
              ? edits.printValidUntil
              : customer.printValidUntil,
          )}
          onValueChange={(value) =>
            setEdits((prev) => ({
              ...prev,
              printValidUntil: value === "default" ? null : value === "yes",
            }))
          }
        >
          <SelectTrigger className="h-7 w-full border-grey4 bg-grey2 text-xs text-white shadow-none focus:ring-0 focus:ring-offset-0">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="default">
              {translate("MC_MD_TRISTATE_DEFAULT")}
            </SelectItem>
            <SelectItem value="yes">
              {translate("MC_MD_TRISTATE_YES")}
            </SelectItem>
            <SelectItem value="no">{translate("MC_MD_TRISTATE_NO")}</SelectItem>
          </SelectContent>
        </Select>
      ) : customer.printValidUntil == null ? (
        translate("MC_MD_TRISTATE_DEFAULT")
      ) : customer.printValidUntil ? (
        translate("MC_MD_TRISTATE_YES")
      ) : (
        translate("MC_MD_TRISTATE_NO")
      ),
    } satisfies FieldDef,
    {
      label: translate("MC_MD_FIELD_PREFERRED_RESPONSE_FORMAT"),
      value: editing ? (
        <Input
          value={
            edits.preferredResponseFormat ??
            customer.preferredResponseFormat ??
            ""
          }
          onChange={(e) =>
            setEdits((prev) => ({
              ...prev,
              preferredResponseFormat: e.target.value,
            }))
          }
          className="h-7 w-full border-grey4 bg-grey2 text-xs text-white shadow-none focus-visible:border-yellow1 focus-visible:ring-0 focus-visible:ring-offset-0"
        />
      ) : (
        (customer.preferredResponseFormat ?? "—")
      ),
    },
  ]

  return (
    <div ref={fieldsRef} className="flex flex-col">
      <CollapsibleSection
        label={translate("MC_MD_FIELDS_SECTION")}
        open={editing || fieldsOpen}
        onOpenChange={setFieldsOpen}
      >
        <FieldsGrid fields={fields} />
      </CollapsibleSection>

      {customer.additionalAttributes && (
        <AdditionalAttributes attributes={customer.additionalAttributes} />
      )}

      {editing && (
        <EditActionBar
          onCancel={handleCancel}
          onSave={handleSave}
          isPending={isPending}
        />
      )}
    </div>
  )
}
